import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { MissionProgress } from "@/components/MissionProgress";
import { LETTERS, LETTER_ORDER } from "@/data/letters";
import { CheckCircle2, Lock, Award, Star } from "lucide-react";

function StatusPill({ progress }) {
  if (progress?.mastered)
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-3 py-1 text-xs font-bold text-[#047857]"><CheckCircle2 size={13} /> Mastered</span>;
  if (!progress?.unlocked)
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#94A3B8]/20 px-3 py-1 text-xs font-bold text-[#64748B]"><Lock size={13} /> Locked</span>;
  if (progress?.attempts > 0)
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-3 py-1 text-xs font-bold text-[#B45309]">Practice</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#0284C7]/15 px-3 py-1 text-xs font-bold text-[#0369A1]">Ready</span>;
}

export default function ProgressPage() {
  const { progress, refreshProgress } = useApp();
  const navigate = useNavigate();
  useEffect(() => { refreshProgress && refreshProgress(); }, [refreshProgress]);
  const mastered = progress.filter((p) => p.mastered).length;
  const allMastered = mastered === LETTER_ORDER.length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">My Progress</h1>
      <p className="mt-2 font-body text-[#6B7280]">Track your cursive journey and see your rewards.</p>

      <div className="mt-6">
        <MissionProgress mastered={mastered} total={LETTER_ORDER.length} />
      </div>

      {allMastered && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-6 flex items-center gap-4 rounded-3xl border-2 border-[#F59E0B] bg-gradient-to-r from-[#F59E0B]/10 to-[#D97706]/5 p-6"
          data-testid="starter-badge"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-b from-[#F59E0B] to-[#D97706] text-white shadow-md">
            <Award size={32} />
          </div>
          <div>
            <div className="font-heading text-lg font-extrabold text-[#1F2937]">Cursive Starter Badge Earned!</div>
            <div className="font-body text-sm text-[#6B7280]">You mastered a, b, and c. Amazing work!</div>
          </div>
        </motion.div>
      )}

      <div className="mt-6 space-y-3">
        {LETTER_ORDER.map((letter) => {
          const p = progress.find((x) => x.letter === letter);
          return (
            <button
              key={letter}
              onClick={() => p?.unlocked && navigate(`/lesson/${letter}`)}
              data-testid={`progress-row-${letter}`}
              className={`flex w-full items-center justify-between rounded-2xl border border-[#E5DECF] bg-white p-4 text-left transition-all ${
                p?.unlocked ? "hover:border-[#0D9488] hover:shadow-md" : "opacity-70"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="font-cursive text-4xl text-[#0D9488] w-12 text-center">{letter}</span>
                <div>
                  <div className="font-heading font-bold text-[#1F2937]">{LETTERS[letter].displayName}</div>
                  <div className="text-sm text-[#6B7280]">
                    Attempts: {p?.attempts || 0} · Best: {p?.best_score || 0}%
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {p?.mastered && (
                  <div className="hidden sm:flex gap-0.5">
                    {[0, 1, 2].map((i) => <Star key={i} size={16} fill="#F59E0B" color="#F59E0B" />)}
                  </div>
                )}
                <StatusPill progress={p} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
